import { Box, Typography } from "@mui/material";
import { MerchantStatusCell } from "Components/Hunter/Merchants/MerchantsList/MerchantStatusCell";
import { SettlementPaymentCell } from "Components/Users/UserListTable/SettlementPaymentCell";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import {
  type MerchantListItemDto,
  MerchantStatus,
  SettlementStatus,
} from "Infrastructure/Api/Api";
import { formatPhone } from "Utils/PhoneUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { type ReactNode, useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";

const COMPONENT_RESOURCES = Resources.Hunter.Merchants.ListPage.Table;

type Props = {
  isLoading?: boolean;
  data?: Array<MerchantListItemDto>;
  title?: ReactNode;
};

export const HunterDetailMerchantsTable = ({
  isLoading,
  data = [],
  title,
}: Props) => {
  const { t } = useResource();
  const getResponsiveValue = useResponsiveValue();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();

  const columns = useMemo<Array<MRT_ColumnDef<MerchantListItemDto>>>(() => {
    return [
      {
        accessorKey: "merchantStatus",
        header: t(COMPONENT_RESOURCES.Columns.MerchantStatus),
        size: 1,
        Cell: ({ row }) => (
          <MerchantStatusCell status={row.original.merchantStatus} />
        ),
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(
              Resources.Merchant.MerchantStatus[
                MerchantStatus.InvitedWaitingForConfirmation
              ],
            ),
            value: MerchantStatus.InvitedWaitingForConfirmation,
          },
          {
            label: t(Resources.Merchant.MerchantStatus[MerchantStatus.Active]),
            value: MerchantStatus.Active,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus[MerchantStatus.Onboarding],
            ),
            value: MerchantStatus.Onboarding,
          },
        ],
        filterFn: (row, _id, filterValue) =>
          filterValue === row.original.merchantStatus,
      },
      {
        accessorKey: "party.companyName",
        header: t(COMPONENT_RESOURCES.Columns.Party.CompanyName),
        size: getResponsiveValue([150, 200]),
        Cell: ({ cell }) => (
          <Typography fontSize={12} fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: "license.licenseNo",
        header: t(COMPONENT_RESOURCES.Columns.LicenseNumber),
        size: 110,
        Cell: ({ cell }) => {
          const licenseNo = cell.getValue<string>();
          return (
            <Typography fontSize={12} fontWeight={400} noWrap>
              {licenseNo || "-"}
            </Typography>
          );
        },
      },
      {
        // String accessor keeps the PhoneNumber column sortable and searchable.
        accessorFn: ({ party }) => formatPhone(party?.phone),
        id: "party.phone",
        header: t(COMPONENT_RESOURCES.Columns.Party.Phone),
        size: getResponsiveValue([120, 140]),
      },
      {
        accessorKey: "party.email",
        header: t(COMPONENT_RESOURCES.Columns.Party.Email),
        size: 250,
      },
      {
        accessorKey: "transactionsCount",
        header: t(COMPONENT_RESOURCES.Columns.TransactionsCount),
        size: 170,
      },
      {
        accessorKey: "party.settlementStatus",
        header: t(COMPONENT_RESOURCES.Columns.Party.SettlementStatus),
        size: 160,
        Cell: ({ row }) => <SettlementPaymentCell party={row.original.party} />,
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Paid
              ],
            ),
            value: SettlementStatus.Paid,
          },
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Unpaid
              ],
            ),
            value: SettlementStatus.Unpaid,
          },
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Overdue
              ],
            ),
            value: SettlementStatus.Overdue,
          },
        ],
        filterFn: (row, _id, filterValue) => {
          return row.original.party.settlementStatus === filterValue;
        },
      },
    ];
  }, [getResponsiveValue, t]);

  const table = useMaterialReactTable({
    data,
    columns,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    initialState: {
      pagination: { pageSize: 15, pageIndex: 0 },
      density: "compact",
    },
    state: {
      isLoading,
    },
    muiTableBodyRowProps: ({ row }) => {
      const url = `${appUrl("merchant-detail")}/${row.original.publicID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: {
          cursor: "pointer",
        },
      };
    },
    muiTopToolbarProps: {
      sx: {
        "& > div": {
          p: 0,
        },
      },
    },
    renderTopToolbarCustomActions: () =>
      title ? (
        <Box ml={3} display="flex" alignSelf="center">
          <Typography fontWeight={500} lineHeight={1.4} fontSize={[14, 16, 18]}>
            {title}
          </Typography>
        </Box>
      ) : null,
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
  });

  return <MaterialReactTable table={table} />;
};
